import { useState, useEffect } from 'react';
import { analyticsService, LOIAnalytics, IRCAnalytics, ProjectStats, CompletesProgress } from '../services/analyticsService';
import { BarChart3, RefreshCw, CheckCircle, AlertTriangle, XCircle, Trophy, Clock, Target } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, ReferenceLine } from 'recharts';
import { formatDate } from '@/utils/dateFormat';

interface ProjectAnalyticsProps {
  projectId: string;
  projectName: string;
  // Bump this to force a re-fetch (e.g. after the project's total target is saved)
  refreshKey?: number;
}

const ProjectAnalytics: React.FC<ProjectAnalyticsProps> = ({ projectId, projectName, refreshKey }) => {
  const [analytics, setAnalytics] = useState<{
    loiAnalytics: LOIAnalytics;
    ircAnalytics: IRCAnalytics;
    projectStats: ProjectStats;
    completesProgress?: CompletesProgress;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    fetchAnalytics();
  }, [projectId, refreshKey]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      console.log(`🔄 Fetching analytics for projectId: ${projectId}`);
      const data = await analyticsService.getProjectAnalytics(projectId);
      console.log(`✅ Analytics data received:`, data);
      setAnalytics(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error(`❌ Analytics error for projectId ${projectId}:`, err);
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <XCircle className="w-5 h-5 text-red-600 mr-2" />
          <div>
            <h3 className="text-red-800 font-medium">Error loading analytics</h3>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
          <button
            onClick={fetchAnalytics}
            className="ml-auto bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return null;
  }

  const { loiAnalytics, ircAnalytics, projectStats, completesProgress } = analytics;
  const loiStatus = analyticsService.getLOIStatus(loiAnalytics.mean);
  const ircStatus = analyticsService.getIRCStatus(ircAnalytics.incidenceRate);

  // CR-16: completes progress vs goal
  const progressTarget = completesProgress?.target ?? 0;
  const progressCompletes = completesProgress?.totalCompletes ?? projectStats.completed;
  const progressPct = progressTarget > 0 ? Math.round((progressCompletes / progressTarget) * 100) : null;
  const progressSeries = completesProgress?.series ?? [];

  const formatLastUpdated = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Advanced Analytics
        </h3>
        <div className="flex items-center gap-4">
          {lastUpdated && (
            <span className="text-sm text-gray-500">
              Last updated: {formatLastUpdated(lastUpdated)}
            </span>
          )}
          <button
            onClick={fetchAnalytics}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-2"
          >
            <RefreshCw className="w-5 h-5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Completes Progress (CR-16) */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Target className="w-5 h-5" />
            Completes Progress
          </h4>
          <div className="text-right">
            <p className="text-2xl font-bold text-[#18A5F9]">
              {progressCompletes}
              {progressTarget > 0 && (
                <span className="text-gray-400 font-semibold"> / {progressTarget}</span>
              )}
            </p>
            {progressPct !== null ? (
              <p className={`text-xs font-semibold ${progressPct >= 100 ? 'text-emerald-600' : 'text-gray-500'}`}>
                {progressPct}% of goal
              </p>
            ) : (
              <p className="text-xs text-gray-400">No goal set</p>
            )}
          </div>
        </div>

        {progressSeries.length === 0 ? (
          <div className="text-center py-10 text-sm text-gray-500">
            No completes recorded yet — the progress chart will appear once respondents start completing.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={progressSeries} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
              <defs>
                <linearGradient id="completesFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#18A5F9" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#18A5F9" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#ECEEF1" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12, fill: '#6B7280' }}
                tickFormatter={(value: string) => formatDate(value)}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 12, fill: '#6B7280' }}
                domain={[0, (dataMax: number) => Math.max(dataMax, progressTarget)]}
              />
              <Tooltip
                cursor={{ stroke: '#18A5F9', strokeWidth: 1 }}
                labelFormatter={(label: string) => formatDate(label)}
                formatter={(value: any) => [`${value} completes`, 'Cumulative']}
              />
              {progressTarget > 0 && (
                <ReferenceLine
                  y={progressTarget}
                  stroke="#10B981"
                  strokeDasharray="5 4"
                  label={{ value: `Goal ${progressTarget}`, position: 'insideTopRight', fill: '#10B981', fontSize: 12 }}
                />
              )}
              <Area
                type="monotone"
                dataKey="completes"
                name="Completes"
                stroke="#18A5F9"
                strokeWidth={2}
                fill="url(#completesFill)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* LOI Analytics */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Length of Interview (LOI)
        </h4>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">Mean</p>
            <p className="text-2xl font-bold text-blue-600">
              {analyticsService.formatLOI(loiAnalytics.mean)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">Median</p>
            <p className="text-2xl font-bold text-blue-600">
              {analyticsService.formatLOI(loiAnalytics.median)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">Range</p>
            <p className="text-lg font-semibold text-gray-700">
              {analyticsService.formatLOI(loiAnalytics.min)} - {analyticsService.formatLOI(loiAnalytics.max)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">Completions</p>
            <p className="text-2xl font-bold text-gray-700">{loiAnalytics.count}</p>
          </div>
        </div>

        <div className={`inline-flex items-center px-3 py-2 rounded-lg ${loiStatus.color}`}>
          <span className="mr-2">
            {loiStatus.status === 'good' ? <CheckCircle className="w-5 h-5" /> : 
             loiStatus.status === 'average' ? <AlertTriangle className="w-5 h-5" /> : 
             <XCircle className="w-5 h-5" />}
          </span>
          <div>
            <p className="font-medium capitalize">{loiStatus.status}</p>
            <p className="text-xs">{loiStatus.description}</p>
          </div>
        </div>
      </div>

      {/* IRC Analytics */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Incidence Rate & Completion
        </h4>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">Incidence Rate</p>
            <p className="text-2xl font-bold text-green-600">
              {ircAnalytics.incidenceRate.toFixed(2)}%
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">Completion Rate</p>
            <p className="text-2xl font-bold text-green-600">
              {ircAnalytics.completionRate.toFixed(2)}%
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">Started</p>
            <p className="text-2xl font-bold text-gray-700">{ircAnalytics.startedCount}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">Completed</p>
            <p className="text-2xl font-bold text-gray-700">{ircAnalytics.completedCount}</p>
          </div>
        </div>

        <div className={`inline-flex items-center px-3 py-2 rounded-lg ${ircStatus.color}`}>
          <span className="mr-2">
            {ircStatus.status === 'excellent' ? <Trophy className="w-5 h-5" /> : 
             ircStatus.status === 'good' ? <CheckCircle className="w-5 h-5" /> : 
             ircStatus.status === 'average' ? <AlertTriangle className="w-5 h-5" /> : 
             <XCircle className="w-5 h-5" />}
          </span>
          <div>
            <p className="font-medium capitalize">{ircStatus.status}</p>
            <p className="text-xs">{ircStatus.description}</p>
          </div>
        </div>
      </div>

      {/* Project Status Overview — IR + mean LOI pulled onto the tiles, simplified colours (CR-07) */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Project Status Overview
        </h4>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-3">
          <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-100">
            <p className="text-2xl font-bold text-[#18A5F9]">{ircAnalytics.incidenceRate.toFixed(1)}%</p>
            <p className="text-xs text-gray-500 mt-1">Incidence Rate</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-100">
            <p className="text-2xl font-bold text-[#18A5F9]">{analyticsService.formatLOI(loiAnalytics.mean)}</p>
            <p className="text-xs text-gray-500 mt-1">Mean LOI</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-100">
            <p className="text-2xl font-bold text-gray-800">{projectStats.total}</p>
            <p className="text-xs text-gray-500 mt-1">Total</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-100">
            <p className="text-2xl font-bold text-emerald-600">{projectStats.completed}</p>
            <p className="text-xs text-gray-500 mt-1">Completed</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-100">
            <p className="text-2xl font-bold text-gray-800">{projectStats.terminated}</p>
            <p className="text-xs text-gray-500 mt-1">Terminated</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-100">
            <p className="text-2xl font-bold text-gray-800">{projectStats.quotaFull}</p>
            <p className="text-xs text-gray-500 mt-1">Quota Full</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-100">
            <p className="text-2xl font-bold text-gray-800">{projectStats.securityTerminated}</p>
            <p className="text-xs text-gray-500 mt-1">Security</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-100">
            <p className="text-2xl font-bold text-gray-800">{projectStats.qualityTerminated ?? 0}</p>
            <p className="text-xs text-gray-500 mt-1">Quality</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-100">
            <p className="text-2xl font-bold text-gray-800">{projectStats.invited}</p>
            <p className="text-xs text-gray-500 mt-1">Invited</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectAnalytics;